<template>
   <!-- modals teleport into this space -->
   <div id="modals"></div>

   <div class="h-full">
      <AccountRepBanner
         v-if="accountRepresentationBanner.isActive"
         :userNameOrEmail="accountRepresentationBanner.userNameOrEmail"
      />
      <RouterView />

      <SiteCookies />
   </div>

   <!-- Output location for SanoUI toasts -->
   <SnNotificationToaster />
</template>

<script setup lang="ts">
import "@/assets/main.scss";

import { useI18n } from "@/vue-i18n/vue-i18n";
import { RouterView, useRoute } from "vue-router";
import { useHead, useSeoMeta } from "@unhead/vue";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { useAppStore } from "@/stores/appStore";
import { useUserStore } from "./stores/userStore";
import { intercomKey } from "@/plugins/useIntercom";
import { useBaseContentStore } from "@/stores/baseContentStore";
import { useAuthStore } from "@/stores/authStore";
import { useApiRequest } from "@/composables/useApiRequest";
import { useCookieSettingsStore } from "@/stores/cookieSettingsStore";
import { SnNotificationToaster } from "@sano-ui-library/src";
import { trackSignUpCompleted } from "@/js/services/metaPixel.service";

const i18n = useI18n();
const intercom = inject(intercomKey);
const clientSettings = inject(useClientSettingsKey);
const organisation = import.meta.env.VITE_APP_ORG;
const route = useRoute();
const cookieSettingsStore = useCookieSettingsStore();
const appStore = useAppStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const baseContentStore = useBaseContentStore();
const { isLoading, loadErrors, execRequest, execManyRequests } = useApiRequest();

const $router = useRouter();

// data setup
// -----------------------------------------
// set the store visit query params
appStore.setUrlQueryParams();

// lifecycle
// -----------------------------------------
onMounted(async () => {
   if (authStore.isAuthTokenAndDCValid()) {
      await execManyRequests([() => baseContentStore.setContent(), () => userStore.setUser()]);
   } else {
      await execRequest(() => baseContentStore.setContent());
   }

   if (loadErrors.value.length > 0) {
      console.error(loadErrors.value);
      throw new Error("App loading error");
   }

   checkRegistrationSuccessParams();
   bootIntercomService();

   // populate the Cookie Settings store with the cookie categories based on the Portal client settings
   cookieSettingsStore.populateCookieCategories(clientSettings, i18n.t);
});

// computed
// -----------------------------------------
const pageMeta = computed(() => {
   if (!baseContentStore.content) return null;
   return baseContentStore.content.pageMeta;
});

// is the user being represented by a carer/parent or is the account being impersonated by an admin.
const accountRepresentationBanner = computed(() => {
   // wait for user to be fetched otherwise return null
   if (isLoading.value) {
      return {
         isActive: false,
         userNameOrEmail: null,
      };
   }

   // if the user is being impersonated by an admin, show the banner
   if (userStore.isImpersonating) {
      return {
         isActive: true,
         userNameOrEmail: authStore.getUserEmail(),
      };
   }
   // if the user is being represented by a carer/parent, show the banner
   else if (userStore.user?.isRepresented) {
      // set this in the store so it can be used across other component (flowselNav, Nav etc.)
      userStore.isAccountRepresented = true;
      return {
         isActive: true,
         userNameOrEmail: userStore.user.fullname,
      };
   }
   // default is false
   else {
      return {
         isActive: false,
         userNameOrEmail: null,
      };
   }
});

// methods
// -----------------------------------------
// intercom - disable if user cookie preference (in privacy) has been set
function bootIntercomService() {
   intercom.bootIntercom(authStore.getUserEmail(), i18n.locale.value, userStore.user?.intercomHash || null);
}

// handle the returning user param
function checkRegistrationSuccessParams() {
   const googleRegistrationSuccess = (route.query?.signup_result as string) === "signed-up";

   if (googleRegistrationSuccess) {
      // fire meta pixel event
      const signUpStudyId = route.query?.study_id as string | undefined;
      // fire meta pixel event if we have study id from through registration
      if (signUpStudyId) {
         trackSignUpCompleted(signUpStudyId);
      }

      // clear session storage
      setTimeout(() => {
         // delete visit query params from local storage
         appStore.deleteVisitWebStorage();
      }, 300);
   }
}

// app global meta (note that you will still see a favicon error running vite locally in the browser which can be ignored)
// -----------------------------------------
useHead({
   link: [
      {
         rel: "icon",
         href: `/favicons/${organisation}/favicon.ico`,
      },
   ],
   ...(!import.meta.env.SSR &&
      clientSettings.plausibleAnalytics.isActive && {
         script: [
            {
               defer: true,
               src: "https://plausible.io/js/script.js",
               "data-domain": window.location.hostname,
            },
         ],
      }),
});

// Meta or defaults
useSeoMeta({
   title: () => pageMeta.value?.ogTitle || i18n.t("meta.app.welcome"),
   description: () => pageMeta.value?.description || "",
   ogDescription: () => pageMeta.value?.ogDescription || "",
   ogImage: () => pageMeta.value?.ogImage || "",
   ogTitle: () => pageMeta.value?.ogTitle || i18n.t("meta.app.welcome"),
   twitterCard: () => pageMeta.value?.twitterCard || "summary_large_image",
});

// Global Unhandled Promise Rejection Handler
if (!import.meta.env.SSR) {
   setupUnhandledPromises();
}
function setupUnhandledPromises() {
   window.addEventListener("unhandledrejection", (event) => {
      console.error("🪖 Unhandled Promise Rejection:", event.reason);

      // any unhandled promise rejections will be caught here
      if (event.reason?.message !== "Request failed with status code 401") $router.push({ name: "error-page" });
   });
}
</script>
